import { ElementRef, Injectable, NgZone, QueryList } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CareConneCondition, CareConneSenderByStudent, NoteString } from "@app/models/note.model";
import { DialogData } from "@app/shared/components/confirm-dialog/confirm-dialog.component";
import { AuthSelectors } from "@app/store/auth/auth.selectors";
import { RecordSelectors } from "@app/store/record/record.selectors";
import { LibNoteTitleComponent } from "@care-connect-japan/ckweblib";
import { NoteTitleInputParams } from "@care-connect-japan/ckweblib/lib/data/schema/params/library-params";
import { Store } from "@ngxs/store";
// import { CaptureContext, preCache, snapdom } from "@zumer/snapdom";
import { Observable, skip, Subject, takeUntil } from "rxjs";
import { ApiService, CareConneRelateRes } from "../api.service";
import { DialogService } from "../dialog.service";
import { WebviewService } from "../webview.service";
import { HttpStatusCode } from "@angular/common/http";
import { MESSAGES } from "@app/shared/common/constants";
export interface PDFConfigure {
    orientation?: "p" | "portrait" | "l" | "landscape" | undefined,
    unit?: "mm" | "pt" | "px" | "in" | "cm" | "ex" | "em" | "pc" | undefined,
    formatStr?: "mm" | "pt" | "px" | "in" | "cm" | "ex" | "em" | "pc" | undefined,
    isCompress?: boolean
}

@Injectable({
    providedIn: 'root'
})
export class CareConneService {

    public pdfViewElement!: ElementRef<HTMLElement>;
    public libNoteRender = new QueryList<LibNoteTitleComponent>();
    private _renderDataSubject!: Subject<NoteTitleInputParams | undefined>;
    private _renderData$: Observable<NoteTitleInputParams | undefined>;
    private _onDestroyModal!: Subject<void>;

    get onDestroyModal() {
        return this._onDestroyModal;
    }

    get renderPDFSubject() {
        return this._renderDataSubject;
    }

    get renderPDF$() {
        return this._renderData$;
    }

    get OS() {
        return this.webviewService.getMobileOS();
    }

    constructor(
        private store: Store,
        private apiService: ApiService,
        private dialog: DialogService,
        private router: Router,
        private route: ActivatedRoute,
        private ngZone: NgZone,
        private webviewService: WebviewService
    ) {
        this._onDestroyModal = new Subject();
        this._renderDataSubject = new Subject();
        this._renderData$ = this._renderDataSubject.asObservable();
    }

    defaultError(msg?: string) {
        const error: CareConneRelateRes = {
            status: false,
            message: msg || '',
            statusCode: -1
        }
        return error;
    }
    defaultExceedError() {
        let errRes: CareConneRelateRes = {
            status: false,
            message: MESSAGES.CARECONNE.ERR_CONTENT_TOO_LARGE,
            statusCode: 413
        }
        return errRes;
    }
    defaultMemoryError() {
        let errRes: CareConneRelateRes = {
            status: false,
            message: `メモリ不足のため、\nPDF処理を完了できません。`,
            statusCode: 413
        }
        return errRes;
    }

    // async iniPDFComponent(renderEl: ElementRef<HTMLElement>, noteTitleRender: QueryList<LibNoteTitleComponent>) {
    //     if (renderEl) {
    //         this.pdfViewElement = renderEl;
    //     }
    //     if (noteTitleRender) {
    //         this.libNoteRender = noteTitleRender;
    //     }
    //     await this.warmUp();
    //     let _preCacheOption = {
    //         root: document.body
    //     }
    //     await preCache(_preCacheOption as any);

    // }
    // async warmUp() {
    //     const warm = document.createElement('div');
    //     document.body.appendChild(warm);
    //     await document.fonts.ready;
    //     this.ngZone.runOutsideAngular(async () => await snapdom(warm));
    //     warm.remove();
    // }

    pruneDOM(root: HTMLElement) {
        const keep = new Set<Element>();

        function mark(node: Element) {
            keep.add(node);
            let p = node.parentElement;
            while (p) {
                keep.add(p);
                p = p.parentElement;
            }
        }

        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_ELEMENT,
            null
        );

        let node: Element | null;

        while ((node = walker.nextNode() as Element)) {

            const cs = window.getComputedStyle(node);
            const rect = node.getBoundingClientRect();

            if (cs.display === "none" || cs.visibility === "hidden") {
                continue;
            }

            // visible text
            if (node.textContent?.trim()) {
                mark(node);
                continue;
            }

            // form inputs
            if (
                node instanceof HTMLInputElement ||
                node instanceof HTMLTextAreaElement ||
                node instanceof HTMLSelectElement
            ) {
                mark(node);
                continue;
            }

            // media
            if (
                node instanceof HTMLImageElement ||
                node instanceof HTMLCanvasElement ||
                node instanceof SVGElement ||
                node instanceof HTMLVideoElement
            ) {
                mark(node);
                continue;
            }

            // contenteditable
            if ((node as HTMLElement).isContentEditable) {
                mark(node);
                continue;
            }

            // visible box
            if (rect.width > 0 && rect.height > 0) {
                mark(node);
            }
        }

        // remove nodes not marked
        root.querySelectorAll("*").forEach(el => {
            if (!keep.has(el)) {
                el.remove();
            }
        });
    }

    private prettyElement(): HTMLElement | null {
        if (!this.pdfViewElement) return null;
        const nodes = this.pdfViewElement.nativeElement.childNodes as NodeListOf<HTMLElement>;
        const availables = Array.from<HTMLElement>(nodes).filter((x: HTMLElement) => x.tagName && x.tagName.toLowerCase() == 'div') as HTMLElement[];
        if (!availables || !availables[0]) return null;
        const element = availables[0];
        const container = (element.firstChild as HTMLElement)?.querySelector('.form-container') as HTMLElement;
        if (container) {
            element.style.lineHeight = 'initial !important';
            container.classList.add('no-resize');
            return container;
        }
        return null;
    }

    // async pdfGenerator(): Promise<Blob | null> {
    //     let target = this.libNoteRender.first;
    //     await firstValueFrom(
    //         target.isReady$.pipe(
    //             filter(isReady => isReady === true)
    //         )
    //     );
    //     let element = this.prettyElement();
    //     if (!element) return null;
    //     let body = element.querySelector('.form-body') as HTMLElement;
    //     if (!body) return null;
    //     let blob = await this.htmlToPdf(body);
    //     return blob;
    // }

    async createCareConneSend(notes: NoteString[], sdate: string, edate: string, roomIds: number[], message: string, screenName: string): Promise<CareConneRelateRes> {
        let result: CareConneRelateRes = {
            status: false,
            message: MESSAGES.COMMON.NOTE_LIST_LENGTH_ZERO,
            statusCode: HttpStatusCode.Ok
        };
        if (!notes.length) return result;
        try {
            const sendInfo: CareConneCondition = {
                loginnm: this.store.selectSnapshot(AuthSelectors.selectedUnit)?.name || '',
                sdate: sdate,
                edate: edate,
                notes: notes.filter(x => !!x.noteid),
                roomIds,
                message,
                screenName
            }
            const res: CareConneRelateRes = await this.apiService.recordSendToCareConne(sendInfo);
            if (!res.status && res.message) {
                result.message = res.message;
            }

            if (!res.status && !res.message) {
                return this.defaultExceedError();
            }
            result.status = res.status;
            result.statusCode = res.statusCode;
            return result;
        } catch (error) {
            console.error(error);
            result.status = false;
            result.message = error as string;
            result.statusCode = HttpStatusCode.InternalServerError
            return result;
        }
    }

    async pickRooms(daicd?: string): Promise<{ roomIds: number[], message: string } | undefined> {
        this.router.navigate(
            [{ outlets: { modal: ['careconne-rooms'] } }],
            {
                queryParams: { daicd },
                relativeTo: this.route,
                skipLocationChange: true,
                queryParamsHandling: 'merge',
                replaceUrl: true,
            }
        );
        return new Promise((res) => {
            this.store.select(RecordSelectors.currentCareConneSender)
                .pipe(
                    takeUntil(this._onDestroyModal),
                    skip(1)
                )
                .subscribe((resp: any) => {
                    res(resp);
                });
        })

    }

    private checkLandScape(width: number, height: number): boolean {
        return width > height;
    }

    public async sendMessageToS3(msg: string, roomIds: number[], imgLinks: string[]): Promise<CareConneRelateRes> {
        const formData = new FormData();
        const contents = {
            requestContent: msg,
            roomIds,
            imageLinks: imgLinks
        }
        formData.append("data", JSON.stringify(contents));
        let res = await this.apiService.sendMsgToRoomCareConne(formData);
        if (res.message && !res.status) {
            throw (res);
        }
        return res;
    }

    public async pushIdToS3(blob: Blob) {
        const formData = new FormData();
        formData.append("file", blob);
        let response = await this.apiService.sendToS3CareConne(formData);
        if (!response || !response.fileId) {
            return null;
        }
        return ((response?.fileId as number).toString());
    }

    public sendFailDialog(msg?: string) {
        const data: DialogData = {
            cancelText: '',
            excuteText: '閉じる',
            content: `<p style="white-space: break-spaces;'font-size': '12px'">${msg || 'ケアコネ転送に失敗しました。'}</p>`,
            style: {
                'text-align': 'center',
            }
        };
        return this.dialog.confirm(data);
    }

    public sendSuccessDialog() {
        const data: DialogData = {
            cancelText: '',
            excuteText: '閉じる',
            content: `<p>ケアコネに転送しました。</p>`,
            style: {
                'text-align': 'center'
            }

        };
        return this.dialog.confirm(data);
    }

    public async sendFileToCareConne(blob: Blob, shortName: string, filenm: string, roomIds: number[], additionalMsg: string, requestMsg: string) {
        const formData = new FormData();
        formData.append("file", blob);

        const contents = {
            shortName: shortName,
            additionalMessage: additionalMsg,
            requestContent: requestMsg,
            filenm,
            roomIds
        }
        formData.append("data", JSON.stringify(contents));
        let response = await this.apiService.sendToRoomCareConne(formData);
        if (!response.status) {
            throw (response);
        }
        return response.status;
    }

    filterElement(node: Element): boolean {
        const el = node as HTMLElement;
        const cs = el.style;
        const rect = node.getBoundingClientRect();
        if (cs.opacity === "0") {
            return false;
        }
        if (cs.overflow === "hidden" && rect.height === 0) {
            return false;
        }
        if (["SCRIPT", "STYLE", "NOSCRIPT"].includes(el.tagName)) {
            return false;
        }
        // remove hidden elements
        if (cs.display === "none" || cs.visibility === "hidden") {
            return false;
        }

        // keep inputs / textarea / select
        if (
            el instanceof HTMLInputElement ||
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLSelectElement
        ) {
            return true;
        }

        // keep elements containing form controls
        if (el.querySelector("input, textarea, select")) {
            return true;
        }

        // keep media elements
        if (
            el instanceof HTMLImageElement ||
            el instanceof HTMLCanvasElement ||
            el instanceof SVGElement ||
            el instanceof HTMLVideoElement
        ) {
            return true;
        }

        // keep elements with text
        if (el.textContent && el.textContent.trim().length > 0) {
            return true;
        }

        // remove empty spans
        if (el.tagName === "SPAN" && el.getBoundingClientRect().width === 0 && el.getBoundingClientRect().height === 0 && el.children.length === 0) {
            return false;
        }

        // remove invisible zero-size nodes
        if (el.offsetWidth === 0 && el.offsetHeight === 0) {
            return false;
        }

        return true;
    }

    // async canvasCreate(html: HTMLElement): Promise<Blob | null> {
    //     try {
    //         const pageElements = Array.from(
    //             html.querySelectorAll('.page-component:not(lib-figure-mobile .page-component)')
    //         );
    //         if (!pageElements.length) return null;
    //         const { width, height } = pageElements[0].getBoundingClientRect();
    //         const isLandscape = this.checkLandScape(width, height);

    //         const doc = new jsPDF(
    //             isLandscape ? 'l' : 'p',
    //             'mm',
    //             'a4',
    //             true
    //         );

    //         return await this.ngZone.runOutsideAngular(async () => {
    //             for (let i = 0; i < pageElements.length; i++) {
    //                 let result = null;
    //                 if (this.OS === OS.IOS) {
    //                     result = await snapdom.toJpg(
    //                         pageElements[i], {
    //                         fast: true,      // Speeds up style computation
    //                         scale: 1,
    //                         cache: 'full',
    //                         outerTransforms: false,
    //                     });
    //                 } else {
    //                     result = await snapdom.toJpg(
    //                         pageElements[i], {
    //                         filter: this.filterElement,
    //                         filterMode: 'remove',
    //                         fast: true,      // Speeds up style computation
    //                         scale: 1,
    //                         cache: 'full',
    //                         outerTransforms: false,
    //                     });
    //                 }
    //                 const pageWidth = doc.internal.pageSize.getWidth();
    //                 const pageHeight = doc.internal.pageSize.getHeight();

    //                 const ratio = Math.min(
    //                     pageWidth / result.width,
    //                     pageHeight / result.height
    //                 );
    //                 const imgWidth = result.width * ratio;
    //                 const imgHeight = result.height * ratio;

    //                 if (i != 0) doc.addPage();
    //                 doc.addImage(result, 'JPG', 0, 0, imgWidth, imgHeight, '', 'FAST');
    //                 result = null;
    //             }
    //             return doc.output('blob');
    //         })

    //     } catch (error) {
    //         console.error('PDF creation failed:', error);
    //         return null;
    //     }
    // }


    // async htmlToPdf(html: HTMLElement): Promise<Blob | null> {
    //     if (this.OS != OS.IOS) {
    //         this.pruneDOM(html);
    //     }
    //     const blob = await new Promise<Blob | null>((resolve) => {
    //         this.ngZone.runOutsideAngular(async () => {
    //             try {
    //                 const result = await this.canvasCreate(html);
    //                 resolve(result);
    //             } catch (error) {
    //                 console.error(error);
    //                 resolve(null);
    //             }
    //         });
    //     });

    //     return blob;
    // }

     async pickRoomsByStudent(daicds: string[], modalId: string): Promise<CareConneSenderByStudent[] | undefined> {
        this.router.navigate(
            [{ outlets: { modal: ['careconne-rooms-by-student'] } }],
            {
                queryParams: { daicds, modalId },
                relativeTo: this.route,
                skipLocationChange: true,
                queryParamsHandling: 'merge',
                replaceUrl: true,
            }
        );
        return new Promise((res) => {
            this.store.select(RecordSelectors.currentCareConneSendersByStudent)
                .pipe(
                    takeUntil(this._onDestroyModal),
                    skip(1)
                )
                .subscribe((resp: any) => {
                    res(resp);
                });
        })

    }
}

@Injectable({ providedIn: 'root' })
export class CpuMonitorService {
    cpuAlert$!: Subject<void>;
    cpuAlert!: Observable<void>;

    constructor(private ngZone: NgZone) {
        this.cpuAlert$ = new Subject();
        this.cpuAlert = this.cpuAlert$.asObservable();
    }

    private forceStopRaise() {
        this.cpuAlert$.next();
    }

    startMonitoring() {
        window.addEventListener('cpu-warning', this.forceStopRaise.bind(this));
    }
    endMonitoring() {
        window.removeEventListener('cpu-warning', this.forceStopRaise.bind(this));
    }
}